import { ArrowLeft, ArrowRight } from '@outdoor-voices/ui';
import cc from 'classcat';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

import { SCROLL } from '../../config/constants';
import css from './perks.module.scss';
import { Perk, Tier } from './types';

type Props = {
  minimal?: boolean;
  perks: Perk[];
  tiers: Tier[];
  title: string;
};
const Perks: React.FC<Props> = ({ minimal = false, perks, tiers, title }) => {
  const [index, setIndex] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const rootRef = useRef(null);

  const scroll = (dir: number) => {
    const { current } = rootRef;
    const width = current.querySelector('.tier').offsetWidth;

    if (!current) {
      return;
    }

    current.scrollTo({ left: current.scrollLeft + width * dir, behavior: 'smooth' });
  };
  // Handle clicks on the next button
  const onNext = () => {
    scroll(1);
  };

  // Handle clicks on the previous button
  const onPrevious = () => {
    scroll(-1);
  };

  // The scrolling logic
  useEffect(() => {
    // Timer, used to detect whether horizontal scrolling is over
    let interval: any = null;
    const { current } = rootRef;

    if (!current) {
      return;
    }

    const onScroll = () => {
      clearTimeout(interval);

      if (!scrolling) {
        setScrolling(true);
      }

      // Renew timer
      interval = setTimeout(() => {
        if (!scrolling) {
          return;
        }

        const width = current.querySelector('.tier').offsetWidth;
        setIndex(Math.ceil(current.scrollLeft / width));
        setScrolling(false);
      }, 100);
    };

    // Scrolling event start
    current.addEventListener(SCROLL, onScroll);

    return () => {
      if (!current) {
        return;
      }

      clearTimeout(interval);
      current.removeEventListener(SCROLL, onScroll);
    };
  }, [scrolling]);

  const checked = (perk: Perk, tier: Tier) => {
    if (perk.title.toLowerCase().indexOf('birthday') > -1) {
      return <span style={{ color: tier.color }}>{tier.birthdayGift}</span>;
    }
    if (tier.perkIds.indexOf(perk.id) > -1) {
      return (
        <div className={css.star} style={{ color: tier.color }}>
          *
        </div>
      );
    }
    return <>&nbsp;</>;
  };
  const disclaimers = perks.map(({ disclaimer }) => disclaimer).filter((perk) => perk !== null);

  return (
    <section className={css.wrap}>
      {!minimal && (
        <div aria-hidden="true" className={css.bg} role="presentation">
          <Image
            alt="People running through a field"
            src="/loyalty/perks-bg.png"
            layout="fill"
            objectFit="cover"
            quality={100}
            priority
          />
        </div>
      )}
      <div className={cc([css.root, { [css.padded]: !minimal }])}>
        <h2 className={cc([css.title, { [css.minimal]: minimal }])}>
          {minimal ? (
            title
          ) : (
            <Image
              alt={title}
              src="/loyalty/shop-to-unlock.png"
              height="167"
              width="1310"
              priority
              layout="responsive"
              objectFit="contain"
            />
          )}
        </h2>
        <div className={cc([css.table, { [css.minimal]: minimal }])}>
          <div className={css.perks}>
            <div className={css.header} aria-hidden="true">
              &nbsp;
            </div>
            {perks.map((perk) => (
              <div className={css.cell} key={`perk-${perk.id}`}>
                {perk.title}
              </div>
            ))}
          </div>
          <div className={css.cols} ref={rootRef}>
            {tiers.map((tier) => (
              <div className={cc(['tier', css.col])} key={`tier-${tier.id}`}>
                <div className={css.header} style={{ backgroundColor: tier.color }}>
                  <div className={css.tierTitle}>{tier.title}</div>
                  <div className={css.tierSpend}>{tier.spend}</div>
                </div>
                {perks.map((perk) => (
                  <div
                    className={cc([css.cell, { [css.dolla]: perk.title.toLowerCase().indexOf('birthday') > -1 }])}
                    key={`perk-tier-${perk.id}`}
                  >
                    {checked(perk, tier)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {disclaimers.length > 0 && (
            <div className={cc([css.disclaimers, { [css.minimal]: minimal }])}>
              {disclaimers.map((disclaimer) => (
                <div key={disclaimer}>{disclaimer}</div>
              ))}
            </div>
          )}
          <div className={css.actions}>
            {index > 0 && (
              <button
                aria-label="Click to view previous tier"
                className={cc([css.button, css.prev, { [css.black]: minimal }])}
                onClick={onPrevious}
                type="button"
              >
                <ArrowLeft />
                Previous
              </button>
            )}
            {index < tiers.length - 1 && (
              <button
                aria-label="Click to view next tier"
                className={cc([css.button, css.next, { [css.black]: minimal }])}
                onClick={onNext}
                type="button"
              >
                Next
                <ArrowRight />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
export default Perks;
